import CodeIcon from "@mui/icons-material/Code";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { Button, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import { Panel } from "@poseidon/ui/components/Panel";
import { SelectDialog } from "@poseidon/ui/components/SelectDialog";
import { ShowMoreDialog } from "@poseidon/ui/components/ShowMoreDialog";
import { niceDate } from "@poseidon/utils/date";
import { JsonViewer } from "@textea/json-viewer";
import { useCallback, useEffect, useState } from "react";
import { CreditCheck } from "./CreditCheck";
import { CreditCheckNew } from "./CreditCheckNew";
import { useCreateCreditCheck } from "./hooks/useCreateCreditCheck";
import { useCreditChecks } from "./hooks/useCreditChecks";
import { TestPersonaPickerDialog } from "./TestPersonaPickerDialog";

const isTestEnvironment =
  process.env.NX_FIREBASE_PROJECT_ID !== "salt-finance-prod-fb791";

const mapCreditCheckToSelectDialogOption = ({ id, requestedAt }) => ({
  label: requestedAt ? niceDate(requestedAt) : "Unknown",
  secondary: requestedAt?.toLocaleString(),
  value: id,
});

export const CreditChecks = ({ sailorId }) => {
  const [creditCheck, setCreditCheck] = useState(null);
  const [personaPickerOpen, setPersonaPickerOpen] = useState(false);
  const { setError } = useErrorMessage();
  const { setToast } = useToastMessage();

  const { creditChecks, loading, error } = useCreditChecks(sailorId);

  const {
    run: runCreateCreditCheck,
    loading: funcLoading,
    funcError,
    success,
  } = useCreateCreditCheck();

  const handleStartCreditCheck = () => {
    if (isTestEnvironment) {
      setPersonaPickerOpen(true);
    } else {
      runCreateCreditCheck({ sailorId });
    }
  };

  const handlePersonaSelect = (personaName) => {
    setPersonaPickerOpen(false);
    runCreateCreditCheck({ sailorId, testPersonaName: personaName });
  };

  useEffect(() => {
    if (!loading && creditChecks.length > 0) {
      setCreditCheck(creditChecks[0]);
    }

    if (error || funcError) {
      setError(error || funcError);
    }
    if (funcLoading) {
      setCreditCheck(null);
    }
    if (success) {
      setToast("Successfully created new Credit Check");
    }
  }, [
    error,
    funcError,
    funcLoading,
    success,
    setError,
    setToast,
    creditChecks,
    loading,
  ]);

  const selectCreditCheckOnChange = useCallback(
    (value) => setCreditCheck(creditChecks.find((check) => check.id === value)),
    [creditChecks]
  );

  return (
    !loading && (
      <Stack spacing={3}>
        <Panel
          title="Credit Check (New)"
          expandable
          actions={
            <>
              {creditCheck && (
                <ShowMoreDialog
                  buttonText="View raw data"
                  buttonVariant="outlined"
                  title="Raw Search Data"
                  buttonProps={{
                    size: "small",
                    startIcon: <CodeIcon />,
                  }}
                >
                  <JsonViewer
                    value={creditCheck ?? {}}
                    displayDataTypes={false}
                  />
                </ShowMoreDialog>
              )}
              <Button
                size="small"
                variant="contained"
                startIcon={<PersonSearchIcon />}
                onClick={handleStartCreditCheck}
              >
                Start {creditChecks?.length ? " New" : ""} Credit Check
              </Button>
            </>
          }
        >
          {creditChecks?.length < 1 && (
            <Typography variant="caption" sx={{ mb: 2, opacity: 0.7 }}>
              No Credit Checks started yet
            </Typography>
          )}
          {creditChecks.length > 0 && creditCheck && (
            <Stack spacing={2} sx={{ my: 1 }}>
              <Typography variant="body2">
                {creditCheck && (
                  <>
                    Check from{" "}
                    {creditCheck?.requestedAt
                      ? `${niceDate(
                          creditCheck.requestedAt
                        )} (${creditCheck.requestedAt.toLocaleString()})`
                      : "Unknown"}
                  </>
                )}
                {creditChecks.length > 1 && (
                  <SelectDialog
                    buttonText="View other"
                    buttonVariant="text"
                    title="Select Credit Check"
                    buttonProps={{ size: "small" }}
                    options={creditChecks}
                    mapOption={mapCreditCheckToSelectDialogOption}
                    onChange={selectCreditCheckOnChange}
                    value={creditCheck}
                  />
                )}
                {creditChecks.length > 1 &&
                  creditCheck?.id !== creditChecks[0].id && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setCreditCheck(creditChecks[0])}
                    >
                      View latest
                    </Button>
                  )}
              </Typography>
              {creditCheck.status === "fetched" && (
                <CreditCheckNew creditCheck={creditCheck} />
              )}
              {creditCheck.status !== "fetched" && (
                <Typography variant="caption" color="text.secondary">
                  Credit check status: {creditCheck.status}
                </Typography>
              )}
            </Stack>
          )}
        </Panel>
        <Panel
          title="Credit Checks"
          expandable
          startCollapsed
          actions={
            <>
              {creditCheck && (
                <ShowMoreDialog
                  buttonText="View raw data"
                  buttonVariant="outlined"
                  title="Raw Search Data"
                  buttonProps={{
                    size: "small",
                    startIcon: <CodeIcon />,
                  }}
                >
                  <JsonViewer
                    value={creditCheck ?? {}}
                    displayDataTypes={false}
                  />
                </ShowMoreDialog>
              )}
              <Button
                size="small"
                variant="contained"
                startIcon={<PersonSearchIcon />}
                onClick={handleStartCreditCheck}
              >
                Start {creditChecks?.length ? " New" : ""} Credit Check
              </Button>
            </>
          }
        >
          {creditChecks?.length < 1 && (
            <Typography variant="caption" sx={{ mb: 2, opacity: 0.7 }}>
              No Credit Checks started yet
            </Typography>
          )}

          {creditChecks.length > 0 && (
            <Stack spacing={3} sx={{ my: 2 }}>
              <Typography variant="body2">
                {creditCheck && (
                  <>
                    Check from{" "}
                    {creditCheck?.requestedAt
                      ? `${niceDate(
                          creditCheck.requestedAt
                        )} (${creditCheck.requestedAt.toLocaleString()})`
                      : "Unknown"}
                  </>
                )}
                {creditChecks.length > 1 && (
                  <SelectDialog
                    buttonText="View other"
                    buttonVariant="text"
                    title="Select Credit Check"
                    buttonProps={{ size: "small" }}
                    options={creditChecks}
                    mapOption={mapCreditCheckToSelectDialogOption}
                    onChange={selectCreditCheckOnChange}
                    value={creditCheck}
                  />
                )}
                {creditChecks.length > 1 &&
                  creditCheck?.id !== creditChecks[0].id && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setCreditCheck(creditChecks[0])}
                    >
                      View latest
                    </Button>
                  )}
              </Typography>
              {creditCheck && <CreditCheck creditCheck={creditCheck} />}
            </Stack>
          )}
        </Panel>
        {isTestEnvironment && (
          <TestPersonaPickerDialog
            open={personaPickerOpen}
            onClose={() => setPersonaPickerOpen(false)}
            onSelect={handlePersonaSelect}
          />
        )}
      </Stack>
    )
  );
};
